import React from "react";
import { useTranslation } from "react-i18next";
import parentServices from "../../../services/parent-services";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import Div from "@jumbo/shared/Div";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

import {
  COUNTRIES,
  findCountryByCode,
  COUNTRIES_I18N,
} from "../../../utils/constants/appActions";
import { useAppMutation } from "../../../services";
import LinearProgress from "@mui/material/LinearProgress";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

/* ---------- Address Step (UI + Validation) ---------- */

const LoadingAdornment = ({ loading }) =>
  loading ? (
    <LinearProgress
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 2,
        borderRadius: 1,
      }}
    />
  ) : null;

const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontFamily: "Nunito, sans-serif",
      fontSize: "14px",
      fontWeight: 700,
      color: "rgb(0, 36, 101)",
      mb: 0.8,
    }}
  >
    {children} {required ? "*" : ""}
  </Typography>
);

const AddressStep = ({ defaultValues, onBack, onContinue, loadingProfile }) => {
  const { t, i18n } = useTranslation();
  const clearErrTimerRef = React.useRef(null);

  const isFr = (i18n.language || "").toLowerCase().startsWith("fr");

  const getCountryLabel = React.useCallback(
    (option) => {
      if (!option) return "";
      if (typeof option === "string") return option;

      // prefer i18n label, fallback to label/code
      return isFr
        ? option.label_fr ||
            option.label_en ||
            option.label ||
            option.code ||
            ""
        : option.label_en ||
            option.label_fr ||
            option.label ||
            option.code ||
            "";
    },
    [isFr],
  );

  const {
    mutate: loadProfile,
    isSuccess: isLoadSuccess,
    data: profile,
    isError: isLoadError,
    isLoading: isProfileLoading,
  } = useAppMutation(parentServices.loadProfile);

  const [values, setValues] = React.useState({
    address1: defaultValues?.address1 || "",
    address2: defaultValues?.address2 || "",
    country: defaultValues?.countryObj || null,
    city: defaultValues?.city || "",
    postalCode: defaultValues?.postalCode || "",
  });

  const [countryRefresh, setCountryRefresh] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({
    address1: "",
    city: "",
    postalCode: "",
    country: "",
  });

  // ✅ for freeSolo typing
  const [countryInput, setCountryInput] = React.useState("");

  React.useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isLoadSuccess && profile) {
      const location =
        profile?.location ||
        profile?.profile?.location ||
        profile?.data?.location ||
        null;

      if (location) {
        const countryObj =
          typeof location.country === "string"
            ? COUNTRIES_I18N.find((c) => c.code === location.country) ||
              findCountryByCode(location.country)
            : location.country;

        setValues({
          address1: location.address1 || "",
          address2: location.address2 || "",
          country: countryObj || null,
          city: location.city || "",
          postalCode: location.postalCode || "",
        });

        // keep typed input aligned
        setCountryInput(getCountryLabel(countryObj) || countryObj?.code || "");
      }

      setCountryRefresh(true);
    }

    if (isLoadError) {
      setCountryRefresh(true);
    }
  }, [isLoadSuccess, isLoadError, profile]);

  React.useEffect(() => {
    // Ensures Autocomplete renders correctly even when defaultValues loads later
    setCountryRefresh(true);

    // keep input aligned with defaultValues if they exist
    const dv = defaultValues?.countryObj;
    if (dv) setCountryInput(dv?.label || dv?.code || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (prop) => (event) => {
    setErrMsg("");
    setFieldErrors((prev) => ({ ...prev, [prop]: "" }));
    setValues((prev) => ({ ...prev, [prop]: event.target.value }));
  };

  // ✅ Updated: supports selecting OR typing a new country
  const handleCountryChange = (event, newValue) => {
    setErrMsg("");
    setFieldErrors((prev) => ({ ...prev, country: "" }));

    // freeSolo => newValue can be string
    if (typeof newValue === "string") {
      const typed = newValue.trim();
      setValues((prev) => ({
        ...prev,
        country: typed ? { code: typed, label: typed } : null,
      }));
      return;
    }

    setValues((prev) => ({ ...prev, country: newValue || null }));
  };

  const continueNext = () => {
    setErrMsg("");

    const nextErrors = { address1: "", city: "", postalCode: "", country: "" };

    if (!values.address1?.trim())
      nextErrors.address1 = t("common.required", "Required");
    if (!values.city?.trim())
      nextErrors.city = t("common.required", "Required");
    if (!values.postalCode?.trim())
      nextErrors.postalCode = t("common.required", "Required");
    if (!values.country) nextErrors.country = t("common.required", "Required");

    const hasError = Object.values(nextErrors).some(Boolean);
    if (hasError) {
      setFieldErrors(nextErrors);
      setErrMsg(t("common.inputError", "Please fill required fields"));

      if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
      clearErrTimerRef.current = setTimeout(() => {
        setFieldErrors({
          address1: "",
          city: "",
          postalCode: "",
          country: "",
        });
      }, 3000);

      return;
    }

    onContinue({
      address1: values.address1?.trim() || "",
      address2: values.address2?.trim() || "",
      country: values.country?.code || "",
      city: values.city?.trim() || "",
      postalCode: values.postalCode?.trim() || "",
      countryObj: values.country || null,
    });
  };

  return (
    <Div
      sx={{
        width: "100%",
        backgroundColor: "rgb(255, 255, 255)",
        borderRadius: "18px",
        px: { xs: 3, sm: 4, md: 3 },
        pt: 3,
        pb: 3,
        minHeight: "auto",
      }}
    >
      <Typography
        sx={{
          display: "block",
          fontFamily: "Nunito, sans-serif",
          fontSize: "20px",
          fontWeight: 800,
          color: "rgb(0, 36, 101)",
          textAlign: "center",
          mb: 2,
        }}
      >
        {t("payment-modal.addressTitle")}
      </Typography>

      <Divider sx={{ mb: 3, borderColor: "rgb(229, 231, 235)" }} />

      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid rgb(229, 231, 235)",
          p: 1.6,
          mb: 2.5,
          backgroundColor: "rgb(229, 229, 229)",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Nunito, sans-serif",
            fontSize: 12.5,
            color: "rgb(107, 114, 128)",
            lineHeight: 1.4,
          }}
        >
          {t("payment-modal.addressSubTitle")}
        </Typography>
      </Paper>

      <Stack spacing={2}>
        <Div>
          <FieldLabel required>{t("payment-modal.address")}</FieldLabel>
          <TextField
            fullWidth
            placeholder="123 Main Street"
            value={values.address1}
            onChange={handleChange("address1")}
            disabled={loadingProfile}
            error={!!fieldErrors.address1}
            helperText={fieldErrors.address1}
            FormHelperTextProps={{ sx: { fontFamily: "Nunito, sans-serif" } }}
            InputProps={{
              endAdornment: <LoadingAdornment loading={isProfileLoading} />,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                height: 44,
                backgroundColor: "rgb(255, 255, 255)",
                fontFamily: "Nunito, sans-serif",
              },
            }}
          />
        </Div>

        <Div>
          <FieldLabel>{t("payment-modal.address2")}</FieldLabel>
          <TextField
            fullWidth
            placeholder="Apartment, floor, etc."
            value={values.address2}
            onChange={handleChange("address2")}
            InputProps={{
              endAdornment: <LoadingAdornment loading={isProfileLoading} />,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                height: 44,
                backgroundColor: "rgb(255, 255, 255)",
                fontFamily: "Nunito, sans-serif",
              },
            }}
          />
        </Div>

        <Div sx={{ display: "flex", gap: 2 }}>
          <Div sx={{ flex: 1 }}>
            <FieldLabel required>{t("payment-modal.city")}</FieldLabel>
            <TextField
              fullWidth
              placeholder="New York"
              value={values.city}
              onChange={handleChange("city")}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city}
              FormHelperTextProps={{ sx: { fontFamily: "Nunito, sans-serif" } }}
              InputProps={{
                endAdornment: <LoadingAdornment loading={isProfileLoading} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  height: 44,
                  backgroundColor: "rgb(255, 255, 255)",
                  fontFamily: "Nunito, sans-serif",
                },
              }}
            />
          </Div>

          <Div sx={{ flex: 1 }}>
            <FieldLabel required>{t("payment-modal.postalCode")}</FieldLabel>
            <TextField
              fullWidth
              placeholder="10001"
              value={values.postalCode}
              onChange={handleChange("postalCode")}
              error={!!fieldErrors.postalCode}
              helperText={fieldErrors.postalCode}
              FormHelperTextProps={{ sx: { fontFamily: "Nunito, sans-serif" } }}
              InputProps={{
                endAdornment: <LoadingAdornment loading={isProfileLoading} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  height: 44,
                  backgroundColor: "rgb(255, 255, 255)",
                  fontFamily: "Nunito, sans-serif",
                },
              }}
            />
          </Div>
        </Div>

        <Div>
          <FieldLabel required>{t("payment-modal.country")}</FieldLabel>

          {countryRefresh && (
            <Autocomplete
              options={COUNTRIES_I18N}
              value={values.country}
              freeSolo
              inputValue={countryInput}
              onInputChange={(e, v) => setCountryInput(v)}
              onChange={(event, newValue) => {
                setErrMsg("");
                setFieldErrors((prev) => ({ ...prev, country: "" }));

                // freeSolo => newValue can be string
                if (typeof newValue === "string") {
                  const typed = newValue.trim();
                  setValues((prev) => ({
                    ...prev,
                    country: typed
                      ? {
                          code: typed,
                          label: typed,
                          label_en: typed,
                          label_fr: typed,
                        }
                      : null,
                  }));
                  return;
                }

                setValues((prev) => ({ ...prev, country: newValue || null }));
              }}
              getOptionLabel={(option) => getCountryLabel(option)}
              isOptionEqualToValue={(option, value) => {
                // compare by code when possible (best), else by translated label
                const oc = typeof option === "string" ? option : option?.code;
                const vc = typeof value === "string" ? value : value?.code;
                if (oc && vc) return String(oc) === String(vc);

                return (
                  String(getCountryLabel(option) || "") ===
                  String(getCountryLabel(value) || "")
                );
              }}
              renderOption={(props, option) => {
                const opt =
                  typeof option === "string"
                    ? {
                        code: option,
                        label: option,
                        label_en: option,
                        label_fr: option,
                      }
                    : option;

                const hasIso2 = opt?.code && String(opt.code).length === 2;
                const label = getCountryLabel(opt);

                return (
                  <Box
                    component="li"
                    sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    {hasIso2 ? (
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${opt.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${opt.code.toLowerCase()}.png 2x`}
                        alt=""
                      />
                    ) : (
                      <Box sx={{ width: 20, mr: 2 }} />
                    )}
                    {label} {hasIso2 ? `(${opt.code})` : ""}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={isFr ? "France" : "France"}
                  error={!!fieldErrors.country}
                  helperText={fieldErrors.country}
                  FormHelperTextProps={{
                    sx: { fontFamily: "Nunito, sans-serif" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: 44,
                      backgroundColor: "rgb(255, 255, 255)",
                      fontFamily: "Nunito, sans-serif",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <LoadingAdornment loading={isProfileLoading} />
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Div>

        <Stack
          direction="row"
          spacing={2}
          sx={{ pt: 1.5, justifyContent: "center" }}
        >
          <Button
            onClick={onBack}
            sx={{
              flex: 1,
              maxWidth: 260,
              backgroundColor: "rgb(224, 224, 224)",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              color: "rgb(102, 102, 102)",
              height: 44,
              "&:hover": { backgroundColor: "rgb(219, 219, 219)" },
            }}
          >
            {t("payment-modal.back")}
          </Button>

          <LoadingButton
            onClick={continueNext}
            sx={{
              flex: 1,
              maxWidth: 260,
              borderRadius: "10px",
              backgroundColor: "rgb(29, 106, 222)",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              color: "rgb(255, 255, 255)",
              height: 44,
              "&:hover": { backgroundColor: "rgb(30, 79, 214)" },
            }}
          >
            {t("payment-modal.continue")}
          </LoadingButton>
        </Stack>
      </Stack>
    </Div>
  );
};

/* ---------- Stripe Payment Step ---------- */

const PaymentModel = ({ subscription, hide, address }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const [errMsg, setErrMsg] = React.useState("");
  const [paymentProcessing, setPaymentProcessing] = React.useState(false);
  const baseUrl = window.location.origin;

  const handleSubmit = async (event) => {
    setPaymentProcessing(true);
    event.preventDefault();

    if (!stripe || !elements) return;

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: baseUrl + "/parent/complete/payment",
        // Optional billing details (safe to include; does not break existing flow)
        payment_method_data: {
          billing_details: {
            address: {
              line1: address?.address1 || "",
              line2: address?.address2 || "",
              city: address?.city || "",
              postal_code: address?.postalCode || "",
              country: address?.country || "",
            },
          },
        },
      },
    });

    if (result.error) {
      setErrMsg(result.error.message);
      setPaymentProcessing(false);
    } else {
      window.location = "/dashboard/subscription";
    }
  };

  return (
    <Div
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: "18px",
        px: { xs: 3, sm: 4, md: 3 },
        pt: 3,
        pb: 3,
        minHeight: "auto",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Nunito, sans-serif",
          fontSize: "20px",
          fontWeight: 800,
          color: "rgb(0, 36, 101)",
          textAlign: "center",
          mb: 2,
        }}
      >
        {t("payment-modal.paymentTitle")}
      </Typography>

      <Divider sx={{ mb: 1, borderColor: "rgb(229, 231, 235)" }} />

      {/* ✅ SINGLE FORM ONLY */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
      >
        <Box sx={{ p: 1.5 }}>
          <PaymentElement />
          {/*<Stack sx={{ width: "100%", mt: 2 }} spacing={2}>
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
          </Stack>*/}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{ pt: 1.5, justifyContent: "center" }}
        >
          <Button
            type="button" // ✅ important so it won't submit
            onClick={() => hide({ backToAddress: true })}
            sx={{
              flex: 1,
              maxWidth: 260,
              backgroundColor: "rgb(224, 224, 224)",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              color: "rgb(102,102,102)",
              height: 44,
              "&:hover": { backgroundColor: "rgb(219, 219, 219)" },
            }}
          >
            {t("payment-modal.back")}
          </Button>

          <LoadingButton
            disabled={!stripe}
            loading={paymentProcessing}
            variant="contained"
            type="submit" // ✅ will now correctly trigger onSubmit
            sx={{
              flex: 1,
              maxWidth: 260,
              bgcolor: "rgb(29,106,222)",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              color: "#fff",
              height: 44,
              "&:hover": { backgroundColor: "rgb(30, 79, 214)" },
            }}
          >
            {t("payment-modal.pay")}
          </LoadingButton>
        </Stack>
      </Box>
    </Div>
  );
};

/* ---------- Main Component: Address First, Then Payment ---------- */

const Payment = ({ subscription, hide }) => {
  const { t, i18n } = useTranslation();

  const [step, setStep] = React.useState("address"); // "address" | "payment"
  const [options, setOptions] = React.useState(null);
  const [address, setAddress] = React.useState(null);

  // Load profile for prefilling address (same behavior as your AddressDetails screen)
  const {
    mutate: loadProfile,
    isSuccess: isLoadSuccess,
    data: profile,
    isLoading: isProfileLoading,
  } = useAppMutation(parentServices.loadProfile);

  React.useEffect(() => {
    if (!!subscription?.subscriptionId) {
      setStep("address");
      setOptions(null);
      setAddress(null);
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const stripeLocale = React.useMemo(() => {
    const lang = (i18n.language || "en").toLowerCase();

    if (lang.startsWith("fr")) return "fr";

    return "en";
  }, [i18n.language]);

  const defaultAddressValues = React.useMemo(() => {
    if (!isLoadSuccess || !profile) return null;

    const location =
      profile?.location ||
      profile?.profile?.location ||
      profile?.data?.location ||
      null;

    if (!location) return null;

    const countryObj =
      typeof location.country === "string"
        ? findCountryByCode(location.country)
        : location.country;

    return {
      address1: location.address1 || "",
      address2: location.address2 || "",
      country:
        typeof location.country === "string"
          ? location.country
          : countryObj?.code || "",
      city: location.city || "",
      postalCode: location.postalCode || "",
      countryObj: countryObj || null,
    };
  }, [isLoadSuccess, profile]);

  // Load stripe client secret ONLY after address step is completed
  React.useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (step !== "payment") return;
      if (!subscription?.subscriptionId) return;

      const response = await parentServices.loadSecret({
        subscriptionId: subscription.subscriptionId,
        // If your backend supports it, you can pass address here too.
        // address,
      });

      if (mounted)
        setOptions({ clientSecret: response.secret, locale: stripeLocale });
    };

    run();

    return () => {
      mounted = false;
    };
  }, [step, subscription]);

  if (!subscription?.subscriptionId) return null;

  return (
    <Dialog
      open={!!subscription?.subscriptionId}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 500, // ✅ increase as you like (600-720)
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogContent
        sx={{
          width: "100%", // ✅ let it fully use Paper width
          p: 0, // ✅ IMPORTANT: remove default padding
          //minHeight: 420,
        }}
      >
        <IconButton
          onClick={() => hide(true)}
          sx={{
            position: "absolute",
            top: 15,
            right: 20,
            zIndex: 10,
            color: "rgb(107,114,128)",
            backgroundColor: "rgb(255,255,255)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            "&:hover": {
              backgroundColor: "rgb(243,244,246)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        {step === "address" && (
          <AddressStep
            defaultValues={defaultAddressValues}
            loadingProfile={isProfileLoading}
            onBack={() => hide(true)}
            onContinue={(addr) => {
              setAddress(addr);
              setStep("payment");
            }}
          />
        )}

        {step === "payment" && options && (
          <Elements stripe={stripePromise} options={options}>
            <PaymentModel
              hide={(val) => {
                // ✅ Back button from Payment screen should go to Address screen
                if (val?.backToAddress) {
                  setStep("address");
                  return;
                }

                // ✅ Otherwise behave like before (close)
                hide(val);
              }}
              subscription={subscription}
              address={address}
            />
          </Elements>
        )}

        {step === "payment" && !options && (
          <Div
            sx={{
              width: "100%",
              backgroundColor: "rgb(255, 255, 255)",
              borderRadius: "18px",
              px: { xs: 3, sm: 4, md: 3 },
              pt: 3,
              pb: 3,
              minHeight: 260,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 420,
                textAlign: "center",
              }}
            >
              {/* Spinner */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: "3px solid rgb(229, 231, 235)",
                    borderTopColor: "rgb(29, 106, 222)",
                    animation: "spin 0.9s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </Box>

              {/* Text */}
              <Typography
                sx={{
                  fontFamily: "Nunito, sans-serif",
                  fontSize: 16,
                  fontWeight: 800,
                  color: "rgb(0, 36, 101)",
                  mb: 0.5,
                }}
              >
                {t("common.loading", "Loading...")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Nunito, sans-serif",
                  fontSize: 13,
                  color: "rgb(107, 114, 128)",
                  mb: 2.5,
                }}
              >
                {t(
                  "payment-modal.loadingSecret",
                  "Preparing secure payment form. This will only take a moment.",
                )}
              </Typography>

              {/* Sleek shimmer card */}
              <Box
                sx={{
                  borderRadius: "14px",
                  border: "1px solid rgb(229, 231, 235)",
                  bgcolor: "rgb(249, 250, 251)",
                  p: 2,
                  overflow: "hidden",
                  position: "relative",
                  mb: 2.5,
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    transform: "translateX(-60%)",
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0) 100%)",
                    animation: "shimmer 1.2s ease-in-out infinite",
                  },
                  "@keyframes shimmer": {
                    "0%": { transform: "translateX(-60%)" },
                    "100%": { transform: "translateX(160%)" },
                  },
                }}
              >
                <Box
                  sx={{
                    height: 12,
                    borderRadius: 8,
                    bgcolor: "rgb(229,231,235)",
                  }}
                />
                <Box
                  sx={{
                    height: 12,
                    mt: 1.2,
                    borderRadius: 8,
                    bgcolor: "rgb(229,231,235)",
                  }}
                />
                <Box
                  sx={{
                    height: 40,
                    mt: 1.8,
                    borderRadius: 10,
                    bgcolor: "rgb(229,231,235)",
                  }}
                />
              </Box>

              {/* Action */}
              <Button
                onClick={() => setStep("address")}
                sx={{
                  fontFamily: "Nunito, sans-serif",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 2,
                  color: "rgb(29, 106, 222)",
                  "&:hover": { backgroundColor: "rgba(29,106,222,0.08)" },
                }}
              >
                {t("payment-modal.backToAddress", "Back to Address")}
              </Button>
            </Box>
          </Div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Payment;
